import type { FractalFamily, FractalSettings, FractalVariant } from "../types/fractal";
import type { KaleidoscopeSettings } from "../types/kaleidoscope";
import type { BlisterSettings } from "../types/settings";
import { getFractalPresetSettingsForFamily } from "./app-presets";
import { normalizeBlisterSettings } from "./blister-settings";
import { DEFAULT_FRACTAL_SETTINGS, isFractalFamily, isFractalVariant, normalizeFractalSettings } from "./fractal-settings";
import {
  DEFAULT_KALEIDOSCOPE_SETTINGS,
  normalizeKaleidoscopeSettings,
} from "./kaleidoscope-settings";

export type ViewMode = "glitter" | "kaleidoscope" | "fractal";

export type SharedAppState = {
  viewMode: ViewMode;
  blisterSettings: BlisterSettings;
  kaleidoscopeSettings: KaleidoscopeSettings;
  fractalSettings: FractalSettings;
};

export type ParsedSharedSettings = {
  viewMode: ViewMode;
  blisterSettings?: BlisterSettings;
  kaleidoscopeSettings?: KaleidoscopeSettings;
  fractalSettings?: FractalSettings;
};

const HASH_PARAM = "blister";
const LEGACY_SHARE_STATE_VERSION = 1;
const LEGACY_COMPACT_SHARE_STATE_VERSION = 2;
const PREVIOUS_COMPACT_SHARE_STATE_VERSION = 3;
const PREVIOUS_FRACTAL_VARIANT_SHARE_STATE_VERSION = 4;
const COMPACT_SHARE_STATE_VERSION = 5;
const GLITTER_MODE_CODE = 0;
const KALEIDOSCOPE_MODE_CODE = 1;
const FRACTAL_MODE_CODE = 2;

type LegacySharedSettingsPayload = {
  v: number;
  mode: ViewMode;
  blister: BlisterSettings;
  kaleidoscope: KaleidoscopeSettings;
  fractal?: FractalSettings;
};

type CompactSharedSettingsPayload = [
  typeof COMPACT_SHARE_STATE_VERSION,
  typeof GLITTER_MODE_CODE | typeof KALEIDOSCOPE_MODE_CODE | typeof FRACTAL_MODE_CODE,
  unknown[],
];

const BLISTER_VALUE_KEYS = [
  "particleSize",
  "speed",
  "count",
  "opacity",
  "additive",
  "speedRandomness",
  "sizeRandomness",
  "glowStrength",
  "blurStrength",
  "glowFraction",
  "blurFraction",
  "windStrength",
  "windFraction",
  "atmosphereIntensity",
  "ambientDriftSpeed",
  "hazeAmount",
  "fieldDistortion",
  "depthSoftness",
  "sparkleCount",
  "sparkleSize",
  "sparkleOpacity",
  "sparkleReactivity",
  "sparkleRandomness",
  "pulseIntensity",
  "pulseFrequency",
  "trailAmount",
  "afterimageAmount",
  "flashSoftness",
  "bloomIntensity",
  "bloomRadius",
  "grainAmount",
  "vignetteAmount",
  "chromaticAberration",
  "despawnZ",
  "spawnZ",
] as const satisfies readonly (keyof BlisterSettings)[];

const KALEIDOSCOPE_VALUE_KEYS = [
  "segments",
  "facetDensity",
  "patternComplexity",
  "centerStructure",
  "outerReflection",
  "layerDepth",
  "rotationSpeed",
  "morphSpeed",
  "shimmerSpeed",
  "pulseStrength",
  "driftAmount",
  "glowStrength",
  "bloomRadius",
  "highlightIntensity",
  "refractionStrength",
  "contrast",
  "sparkleDensity",
  "microFacetStrength",
  "textureAmount",
  "paletteIntensity",
  "colorSeparation",
  "darkLightBalance",
  "sourceImageIndex",
] as const satisfies readonly (keyof KaleidoscopeSettings)[];

function serializeFractalFamily(family: FractalFamily) {
  return family;
}

function serializeFractalVariant(variant: FractalVariant) {
  return variant;
}

function deserializeFractalFamily(value: unknown): FractalFamily | null {
  return isFractalFamily(value) ? value : null;
}

function deserializeFractalVariant(value: unknown): FractalVariant | null {
  return isFractalVariant(value) ? value : null;
}

const LEGACY_KALEIDOSCOPE_VALUE_KEYS = [
  "segments",
  "patternComplexity",
  "centerSize",
  "outerRingPresence",
  "layerDepth",
  "rotationSpeed",
  "morphSpeed",
  "breathingAmount",
  "driftAmount",
  "pulseIntensity",
  "glowStrength",
  "bloomSoftness",
  "diffusion",
  "hazeAmount",
  "contrastSoftness",
  "fineDetailAmount",
  "sparkleDensity",
  "texturePresence",
  "innerPatternStrength",
  "edgeAccentStrength",
  "paletteIntensity",
  "colorSmoothness",
  "highlightStrength",
  "shadowBalance",
] as const;

function encodeBase64Url(value: string) {
  const bytes = new TextEncoder().encode(value);
  let binary = "";

  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }

  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/g, "");
}

function decodeBase64Url(value: string) {
  const normalized = value.replace(/-/g, "+").replace(/_/g, "/");
  const padding = normalized.length % 4;
  const padded = padding ? normalized.padEnd(normalized.length + (4 - padding), "=") : normalized;
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

function isViewMode(value: unknown): value is ViewMode {
  return value === "glitter" || value === "kaleidoscope" || value === "fractal";
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isHexColor(value: unknown): value is string {
  return typeof value === "string" && /^#([0-9a-fA-F]{6})$/.test(value);
}

function getModeCode(viewMode: ViewMode) {
  if (viewMode === "glitter") {
    return GLITTER_MODE_CODE;
  }

  if (viewMode === "kaleidoscope") {
    return KALEIDOSCOPE_MODE_CODE;
  }

  return FRACTAL_MODE_CODE;
}

function getViewModeFromCode(code: number): ViewMode | null {
  if (code === GLITTER_MODE_CODE) {
    return "glitter";
  }

  if (code === KALEIDOSCOPE_MODE_CODE) {
    return "kaleidoscope";
  }

  if (code === FRACTAL_MODE_CODE) {
    return "fractal";
  }

  return null;
}

function serializeBlisterSettings(settings: BlisterSettings) {
  const normalized = normalizeBlisterSettings(settings);
  return [...normalized.colors, ...BLISTER_VALUE_KEYS.map((key) => normalized[key])];
}

function serializeKaleidoscopeSettings(settings: KaleidoscopeSettings) {
  const normalized = normalizeKaleidoscopeSettings(settings);
  return [...normalized.colors, ...KALEIDOSCOPE_VALUE_KEYS.map((key) => normalized[key])];
}

function serializeFractalSettings(settings: FractalSettings) {
  const normalized = normalizeFractalSettings(settings);
  return [
    serializeFractalFamily(normalized.fractalFamily),
    serializeFractalVariant(normalized.fractalVariant),
    ...normalized.colors,
    normalized.voxelMaterialType,
    normalized.voxelRoughness,
  ];
}

function deserializeBlisterSettings(values: unknown[]) {
  if (values.length < 6) {
    return null;
  }

  const colors = values.slice(0, 6);
  if (!colors.every(isHexColor)) {
    return null;
  }

  const partial: Partial<BlisterSettings> = {
    colors,
  };

  BLISTER_VALUE_KEYS.forEach((key, index) => {
    const value = values[index + 6];
    if (value === undefined) {
      return;
    }

    if (key === "additive") {
      partial[key] = (value === true || value === 1) as BlisterSettings[typeof key];
      return;
    }

    if (typeof value === "number") {
      partial[key] = value as BlisterSettings[typeof key];
    }
  });

  return normalizeBlisterSettings(partial);
}

function deserializeKaleidoscopeSettings(values: unknown[]) {
  if (values.length < 6) {
    return null;
  }

  const colors = values.slice(0, 6);
  if (!colors.every(isHexColor)) {
    return null;
  }

  const partial: Partial<KaleidoscopeSettings> = {
    colors,
  };

  KALEIDOSCOPE_VALUE_KEYS.forEach((key, index) => {
    const value = values[index + 6];
    if (typeof value === "number") {
      partial[key] = value as KaleidoscopeSettings[typeof key];
    }
  });

  return normalizeKaleidoscopeSettings(partial);
}

function deserializeFractalSettings(values: unknown[], includeFamily: boolean) {
  const family = includeFamily ? deserializeFractalFamily(values[0]) : null;
  const variant = includeFamily ? deserializeFractalVariant(values[1]) : null;
  const colors = includeFamily ? values.slice(2, 8) : [];
  const voxelMaterialType = includeFamily && typeof values[8] === "number" ? values[8] : undefined;
  const voxelRoughness = includeFamily && typeof values[9] === "number" ? values[9] : undefined;
  return normalizeFractalSettings({
    ...getFractalPresetSettingsForFamily(family ?? DEFAULT_FRACTAL_SETTINGS.fractalFamily),
    fractalVariant: variant ?? DEFAULT_FRACTAL_SETTINGS.fractalVariant,
    ...(colors.length >= 6 && colors.every(isHexColor) ? { colors: colors.slice(0, 6) } : {}),
    ...(voxelMaterialType !== undefined ? { voxelMaterialType } : {}),
    ...(voxelRoughness !== undefined ? { voxelRoughness } : {}),
  });
}

function deserializeLegacyCompactKaleidoscopeSettings(values: unknown[]) {
  if (values.length < 6) {
    return null;
  }

  const colors = values.slice(0, 6);
  if (!colors.every(isHexColor)) {
    return null;
  }

  const legacyValues: Record<string, number> = {};
  LEGACY_KALEIDOSCOPE_VALUE_KEYS.forEach((key, index) => {
    const value = values[index + 6];
    if (typeof value === "number") {
      legacyValues[key] = value;
    }
  });

  const partial: Partial<KaleidoscopeSettings> = {
    colors,
    segments: legacyValues.segments,
    facetDensity: legacyValues.fineDetailAmount,
    patternComplexity: legacyValues.patternComplexity,
    centerStructure: legacyValues.centerSize,
    outerReflection: legacyValues.outerRingPresence,
    layerDepth: legacyValues.layerDepth,
    rotationSpeed: legacyValues.rotationSpeed,
    morphSpeed: legacyValues.morphSpeed,
    shimmerSpeed: legacyValues.diffusion,
    pulseStrength:
      legacyValues.breathingAmount !== undefined && legacyValues.pulseIntensity !== undefined
        ? (legacyValues.breathingAmount + legacyValues.pulseIntensity) / 2
        : (legacyValues.breathingAmount ?? legacyValues.pulseIntensity),
    driftAmount: legacyValues.driftAmount,
    glowStrength: legacyValues.glowStrength,
    bloomRadius: legacyValues.bloomSoftness,
    highlightIntensity:
      legacyValues.highlightStrength !== undefined && legacyValues.edgeAccentStrength !== undefined
        ? legacyValues.highlightStrength * 0.6 + legacyValues.edgeAccentStrength * 0.4
        : (legacyValues.highlightStrength ?? legacyValues.edgeAccentStrength),
    refractionStrength: legacyValues.diffusion,
    contrast:
      legacyValues.contrastSoftness !== undefined
        ? Math.max(0, 1.15 - legacyValues.contrastSoftness * 0.55)
        : undefined,
    sparkleDensity: legacyValues.sparkleDensity,
    microFacetStrength: legacyValues.fineDetailAmount,
    textureAmount: legacyValues.texturePresence,
    paletteIntensity: legacyValues.paletteIntensity,
    colorSeparation:
      legacyValues.colorSmoothness !== undefined
        ? Math.max(0, 1 - legacyValues.colorSmoothness * 0.9)
        : undefined,
    darkLightBalance: legacyValues.shadowBalance,
  };

  if (legacyValues.innerPatternStrength !== undefined) {
    partial.centerStructure = partial.centerStructure !== undefined
      ? partial.centerStructure * 0.65 + legacyValues.innerPatternStrength * 0.35
      : legacyValues.innerPatternStrength;
  }

  if (legacyValues.edgeAccentStrength !== undefined) {
    partial.outerReflection = partial.outerReflection !== undefined
      ? partial.outerReflection * 0.7 + legacyValues.edgeAccentStrength * 0.3
      : legacyValues.edgeAccentStrength;
  }

  if (legacyValues.hazeAmount !== undefined) {
    partial.darkLightBalance = partial.darkLightBalance !== undefined
      ? partial.darkLightBalance * 0.7 + legacyValues.hazeAmount * 0.2
      : 0.45 + legacyValues.hazeAmount * 0.25;
  }

  return normalizeKaleidoscopeSettings(partial);
}

function createCompactPayload(state: SharedAppState): CompactSharedSettingsPayload {
  if (state.viewMode === "glitter") {
    return [
      COMPACT_SHARE_STATE_VERSION,
      GLITTER_MODE_CODE,
      serializeBlisterSettings(state.blisterSettings),
    ];
  }

  if (state.viewMode === "fractal") {
    return [
      COMPACT_SHARE_STATE_VERSION,
      FRACTAL_MODE_CODE,
      serializeFractalSettings(state.fractalSettings),
    ];
  }

  return [
    COMPACT_SHARE_STATE_VERSION,
    KALEIDOSCOPE_MODE_CODE,
    serializeKaleidoscopeSettings(state.kaleidoscopeSettings),
  ];
}

function parseLegacyPayload(decoded: unknown): ParsedSharedSettings | null {
  if (
    !isRecord(decoded) ||
    decoded.v !== LEGACY_SHARE_STATE_VERSION ||
    !isViewMode(decoded.mode)
  ) {
    return null;
  }

  return {
    viewMode: decoded.mode,
    blisterSettings: normalizeBlisterSettings(
      isRecord(decoded.blister) ? (decoded.blister as Partial<BlisterSettings>) : undefined
    ),
    kaleidoscopeSettings: normalizeKaleidoscopeSettings(
      isRecord(decoded.kaleidoscope)
        ? (decoded.kaleidoscope as Partial<KaleidoscopeSettings>)
        : DEFAULT_KALEIDOSCOPE_SETTINGS
    ),
    fractalSettings: getFractalPresetSettingsForFamily(
      isRecord(decoded.fractal) && isFractalFamily(decoded.fractal.fractalFamily)
        ? decoded.fractal.fractalFamily
        : DEFAULT_FRACTAL_SETTINGS.fractalFamily
    ),
  };
}

function parseCompactPayload(decoded: unknown): ParsedSharedSettings | null {
  if (!Array.isArray(decoded) || decoded.length < 3) {
    return null;
  }

  const version = decoded[0];
  if (
    version !== COMPACT_SHARE_STATE_VERSION &&
    version !== PREVIOUS_FRACTAL_VARIANT_SHARE_STATE_VERSION &&
    version !== PREVIOUS_COMPACT_SHARE_STATE_VERSION &&
    version !== LEGACY_COMPACT_SHARE_STATE_VERSION
  ) {
    return null;
  }

  const viewMode = typeof decoded[1] === "number" ? getViewModeFromCode(decoded[1]) : null;
  const values = Array.isArray(decoded[2]) ? decoded[2] : null;

  if (!viewMode || !values) {
    return null;
  }

  if (viewMode === "glitter") {
    const blisterSettings = deserializeBlisterSettings(values);
    return blisterSettings
      ? {
        viewMode,
        blisterSettings,
      }
      : null;
  }

  if (viewMode === "fractal") {
    const fractalSettings = deserializeFractalSettings(values, version === COMPACT_SHARE_STATE_VERSION);
    return fractalSettings
      ? {
        viewMode,
        fractalSettings,
      }
      : null;
  }

  const kaleidoscopeSettings =
    version === COMPACT_SHARE_STATE_VERSION || version === PREVIOUS_COMPACT_SHARE_STATE_VERSION
      ? deserializeKaleidoscopeSettings(values)
      : deserializeLegacyCompactKaleidoscopeSettings(values);
  return kaleidoscopeSettings
    ? {
      viewMode,
      kaleidoscopeSettings,
    }
    : null;
}

export function mergeSharedSettings(
  baseState: SharedAppState,
  parsedSettings: ParsedSharedSettings
): SharedAppState {
  return {
    viewMode: parsedSettings.viewMode,
    blisterSettings: parsedSettings.blisterSettings ?? baseState.blisterSettings,
    kaleidoscopeSettings: parsedSettings.kaleidoscopeSettings ?? baseState.kaleidoscopeSettings,
    fractalSettings: parsedSettings.fractalSettings ?? baseState.fractalSettings,
  };
}

export function buildSettingsHash(state: SharedAppState) {
  const payload = encodeBase64Url(JSON.stringify(createCompactPayload(state)));
  return `#${HASH_PARAM}=${payload}`;
}

function normalizeHashInput(input: string) {
  const value = input.trim();

  if (!value) {
    return "";
  }

  if (value.startsWith("#")) {
    return value;
  }

  if (value.startsWith(`${HASH_PARAM}=`)) {
    return `#${value}`;
  }

  if (/^[A-Za-z0-9_-]+$/.test(value)) {
    return `#${HASH_PARAM}=${value}`;
  }

  try {
    const url = new URL(value);
    if (url.hash) {
      return url.hash;
    }
  } catch {
  }

  const hashIndex = value.indexOf("#");
  if (hashIndex >= 0) {
    return value.slice(hashIndex);
  }

  return value;
}

export function parseSettingsHash(hashInput: string): ParsedSharedSettings | null {
  const hash = normalizeHashInput(hashInput);

  if (!hash) {
    return null;
  }

  const params = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
  const encoded = params.get(HASH_PARAM);

  if (!encoded) {
    return null;
  }

  try {
    const decoded = JSON.parse(decodeBase64Url(encoded)) as unknown;
    return parseCompactPayload(decoded) ?? parseLegacyPayload(decoded);
  } catch {
    return null;
  }
}
